import { FC } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CATEGORY_HINTS,
  KPI,
  KPI_CONCEPTS,
  KpiDefinition,
  KpiGroup,
} from '../../../helpers/webinarKpiDefinitions';

const GROUP_ORDER: KpiGroup[] = ['Personen', 'Anfos', 'Umsatz', 'Quoten', 'Engagement'];
const HEAD = { color: 'black', fontWeight: 'bold', backgroundColor: '#e0e0e0' } as const;
const CELL = { color: 'black', verticalAlign: 'top' } as const;

/**
 * Tab „Kennzahlen & Formeln": jede Kennzahl mit Bedeutung, Rechenweg und Datenquelle. Liest
 * dieselbe Datei wie alle Tooltips — was hier steht, steht auch an der Zahl.
 */
const KpiFormulaSheet: FC = () => {
  const defs = Object.values(KPI) as KpiDefinition[];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Grundregeln
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
            gap: 2,
          }}
        >
          {KPI_CONCEPTS.map((c) => (
            <Paper key={c.titel} sx={{ p: 2, backgroundColor: '#ffffff' }}>
              <Typography sx={{ fontWeight: 700, color: 'black', mb: 0.5 }}>{c.titel}</Typography>
              <Typography variant="body2" sx={{ color: '#333' }}>
                {c.text}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      {GROUP_ORDER.map((group) => (
        <Box key={group}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {group}
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEAD, width: '18%' }}>Kennzahl</TableCell>
                  <TableCell sx={{ ...HEAD, width: '37%' }}>Bedeutung</TableCell>
                  <TableCell sx={{ ...HEAD, width: '25%' }}>Formel</TableCell>
                  <TableCell sx={{ ...HEAD, width: '20%' }}>Datenquelle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defs
                  .filter((d) => d.group === group)
                  .map((d) => (
                    <TableRow key={d.label}>
                      <TableCell sx={{ ...CELL, fontWeight: 600, color: d.color ?? 'black' }}>
                        {d.label}
                        <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                          Spalte: {d.short}
                        </Typography>
                      </TableCell>
                      <TableCell sx={CELL}>{d.tooltip}</TableCell>
                      <TableCell sx={CELL}>{d.formel}</TableCell>
                      <TableCell sx={{ ...CELL, color: '#555' }}>{d.quelle}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <Box>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Sonderzeilen in Tabellen
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEAD, width: '25%' }}>Zeile</TableCell>
                <TableCell sx={HEAD}>Bedeutung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(CATEGORY_HINTS).map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ ...CELL, fontWeight: 600 }}>{k}</TableCell>
                  <TableCell sx={CELL}>{v}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default KpiFormulaSheet;
